import type {Moment} from 'moment-timezone'

import {
  getStoragesFromHistory,
  isHistoryStorageWithMaterialGuard,
  calculateCapacityForStorages,
  calculateDeadStockLevelForStorages,
  calculateMinTargetLevelForStorages
} from '../selectors'
import type {MaterialStoragesHistory} from '../types'

import {useMaterialStorageHistory} from './useMaterialStorageHistory'

export interface MaterialStorageData {
  minTargetLevel: number
  minTargetLevelWeekend: number
  deadStockLevel: number
  storageCapacity: number
}

const selector = (
  storageHistory: MaterialStoragesHistory,
  materialId: number,
  date: Moment
): MaterialStorageData => {
  const storagesWithMaterial = getStoragesFromHistory(storageHistory, date)
    .filter(isHistoryStorageWithMaterialGuard)
    .filter((storage) => storage.materialId === materialId)

  const minTargetLevels = calculateMinTargetLevelForStorages(storagesWithMaterial)
  const deadStockLevel = calculateDeadStockLevelForStorages(storagesWithMaterial)
  const storageCapacity = calculateCapacityForStorages(storagesWithMaterial)
  return {...minTargetLevels, deadStockLevel, storageCapacity}
}

interface MaterialStorageDataQueryParams {
  plantId: string
  timeFrame: [Moment, Moment]
  materialId: number
  date?: Moment
  throwOnError?: boolean
}

export const useMaterialStorageDataFromHistoryQuery = ({
  plantId,
  materialId,
  timeFrame: [from, to],
  date,
  throwOnError = true
}: MaterialStorageDataQueryParams) =>
  useMaterialStorageHistory<MaterialStorageData>({
    plantId,
    from: from.toISOString(),
    to: to.toISOString(),
    options: {
      select: (data) => selector(data, materialId, date ?? to),
      throwOnError
    }
  })
